import ROUTES, { DashboardParams } from 'constants/routes';
import cc from 'classcat';
import React, { Suspense, useMemo, useEffect, useRef, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import C from './CommunityDashboardHeader.css';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useCommunityDetails } from 'atoms/community-details';
import UserAvatar from 'components/user-avatar/UserAvatar';
import { useCommunitySummaries } from 'atoms/community-summaries';
import { useCommunityPresence } from 'atoms/community-presence';
import DotSeparator from 'components/item-list/Separator';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import IconButton from '@mui/material/IconButton';
import { LightTooltip } from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import { qualifiedUserName } from 'utils/user';
import uniqueId from 'lodash/uniqueId';
import { useWindowSize } from 'rooks';
import { NARROW_LAYOUT_THRESHOLD } from 'components/dashboard-layout/breakpoints';
import { Trans } from '@lingui/macro';
import { useTutorial } from 'components/onboarding-tutorials/tutorial-atom';
import { GETTING_STARTED_TUTORIAL } from 'components/onboarding-tutorials/getting-started';
import { ScrollableContainer } from 'components/scrollable-container/ScrollableContainer';
import { EVENTS } from 'utils/mixpanel';
import { PlayCircle } from '@mui/icons-material';
import CocoModal from 'components/coco-modal/coco-modal';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import { LoadingButton } from '@mui/lab';
import { useFlags } from 'utils/feature-flags';

const NewSpaceModal = React.lazy(
  () => import('../new-space-modal/NewSpaceModal'),
);

export default function CommunityDashboardHeader() {
  const { communityId } = useParams<DashboardParams>();
  const { community } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const { communities } = useCommunitySummaries({
    autoLoad: false,
  });
  const [isCreatingSpace, setCreatingSpace] = useState(false);
  const [isPlayingScratchProject, setIsPlayingScratchProject] = useState(false);
  const { onlineCount, presenceInfo } = useCommunityPresence(communityId);
  const memberListContainerRef = useRef<HTMLDivElement | null>(null);
  const [memberListOverflowing, setMemberListOverflowing] = useState({
    left: true,
    right: true,
  });
  useEffect(() => {
    setMemberListOverflowing({
      left: false,
      right: false,
    });
  }, [communityId]);

  const updateMemberListOverflowing = () => {
    const el = memberListContainerRef.current;
    if (!el) return;
    if (el.scrollWidth - el.getBoundingClientRect().width > 5) {
      return setMemberListOverflowing({
        left: el.scrollLeft > 0,
        right: true,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateMemberListOverflowing);
    return () =>
      window.removeEventListener('resize', updateMemberListOverflowing);
  }, []);

  useEffect(() => {
    updateMemberListOverflowing();
  }, [onlineCount]);

  const showSpaceStartBtn = communities.length > 0;
  const showRecentlyActive = onlineCount != null;
  const showDivider = showSpaceStartBtn || showRecentlyActive;

  const { innerWidth } = useWindowSize();
  const isNarrow = !!innerWidth && innerWidth < NARROW_LAYOUT_THRESHOLD;

  const tutorial = useTutorial();
  const [scratchUrl, setScratchUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { play_with_friends } = useFlags(['play_with_friends']);

  const newSpaceBtn = showSpaceStartBtn && (
    <Button
      data-analytics={EVENTS.CREATE_COCO_SPACE}
      className={C.primaryRedBtn}
      variant='contained'
      onClick={() => {
        if (
          tutorial.name === GETTING_STARTED_TUTORIAL &&
          tutorial.stepIndex === 5
        ) {
          tutorial.setStepIndex(6);
        }
        setCreatingSpace(true);
      }}
      style={{
        flexGrow: 0,
        flexShrink: 0,
      }}
      id='coco-new-space-btn'
    >
      <AddIcon style={{ fontSize: '1.25rem' }} />
      <Trans>Create CoCo Space</Trans>
    </Button>
  );

  const playScratchProjectBtn = (
    <Button
      className={C.primaryRedBtn}
      variant='contained'
      onClick={() => {
        if (
          tutorial.name === GETTING_STARTED_TUTORIAL &&
          tutorial.stepIndex === 5
        ) {
          tutorial.setStepIndex(6);
        }
        setIsPlayingScratchProject(true);
      }}
      style={{
        flexGrow: 0,
        flexShrink: 0,
      }}
      id='coco-play-scratch-project-btn'
    >
      <PlayCircle style={{ fontSize: '1.25rem', marginRight: '0.5rem' }} />
      <Trans>Play Scratch Project</Trans>
    </Button>
  );

  const recentlyActiveCount = showRecentlyActive && (
    <div className={C.presenceSummary}>
      <Trans>Recently Active</Trans>
      <DotSeparator />
      <span
        style={{
          color: 'var(--color-primary)',
          fontWeight: 'var(--font-weight-regular)',
        }}
      >
        {onlineCount}
      </span>
    </div>
  );

  const showRecentlyActiveCountRow =
    isNarrow && presenceInfo?.users && presenceInfo.users.length > 1;

  return (
    <>
      {showRecentlyActiveCountRow && (
        <div className={cc([C.container, isNarrow && C.narrow])}>
          {recentlyActiveCount}
        </div>
      )}
      <div className={cc([C.container, isNarrow && C.narrow])}>
        {isNarrow || newSpaceBtn}
        {play_with_friends?.enabled && playScratchProjectBtn}
        {
          <>
            {showRecentlyActiveCountRow ? null : (
              <>
                {recentlyActiveCount}
                {showDivider && (
                  <Divider
                    orientation='vertical'
                    style={{
                      marginRight: '10px',
                      height: '60%',
                    }}
                  />
                )}
              </>
            )}
            <ScrollableContainer
              elements={
                presenceInfo?.users
                  ? presenceInfo?.users.map((user) => {
                    return (
                      <LightTooltip
                        title={qualifiedUserName(user)}
                        arrow
                        placement='bottom'
                        key={user.id}
                      >
                        <span>
                          <UserAvatar
                            user={user}
                            className={C.avatar}
                            size={30}
                            postContent={
                              user.isOnline ? <OnlineIndicator /> : null
                            }
                          />
                        </span>
                      </LightTooltip>
                    );
                  })
                  : community?.members?.map((member) => (
                    <LightTooltip
                      key={member.user?.id ?? uniqueId()}
                      title={
                        member.user
                          ? qualifiedUserName(member.user)
                          : undefined
                      }
                      arrow
                      placement='bottom'
                    >
                      <span>
                        <UserAvatar
                          size={30}
                          user={member.user}
                          className={C.avatar}
                        />
                      </span>
                    </LightTooltip>
                  ))
              }
            />
          </>
        }

        {communityId !== '$all' &&
          routeMatch.path !== ROUTES.COMMUNITY_DASHBOARD_MEMBERS && (
            <Button
              className={C.inlineLink}
              sx={{
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                history.push(`/app/dashboard/${communityId}/members`);
              }}
            >
              <Trans>See all</Trans>
            </Button>
          )}

        {isCreatingSpace && (
          <Suspense fallback={null}>
            <NewSpaceModal
              onClose={() => {
                setCreatingSpace(false);
              }}
              onSuccess={() => {
                setCreatingSpace(false);
              }}
              communities={communities ?? []}
              defaultCommunityId={community?.id}
            />
          </Suspense>
        )}
        {isPlayingScratchProject && (
          <Suspense fallback={null}>
            <CocoModal
              onClose={() => {
                setIsPlayingScratchProject(false);
              }}
              header={<Trans>Play Scratch Project</Trans>}
            >
              <Stack minWidth="400px" p={4}>
                <Typography variant="body1">You can load any scratch project here and play with your friends!</Typography>
                <Box mt={2} />
                <TextField
                  label="https://scratch.mit.edu/projects/"
                  variant="outlined"
                  fullWidth
                  value={scratchUrl}
                  onChange={(e) => setScratchUrl(e.target.value)}
                />
                <Box mt={2} />
                <LoadingButton
                  variant="contained"
                  loading={isLoading}
                  onClick={async () => {
                    setIsLoading(true);
                    const projectId = scratchUrl.split("/").pop();
                    const res = await httpClient.get("/projects/import-scratch-project?projectId=" + projectId);

                    const { spaceId } = res.data;
                    history.push(`/app/spaces/${spaceId}/editor`);
                    setIsLoading(false);
                  }}
                >
                  <Trans>Play</Trans>
                </LoadingButton>
              </Stack>
            </CocoModal>
          </Suspense>
        )}
      </div>
      {isNarrow && (
        <div className={cc([C.container, isNarrow && C.narrow])}>
          {newSpaceBtn}
        </div>
      )}
    </>
  );
}

const OnlineIndicator = () => (
  <div
    style={{
      border: '2px solid var(--color-base)',
      borderRadius: '10px',
      position: 'absolute',
      bottom: 0,
      right: '-5px',
    }}
  >
    <div
      style={{
        border: '4px solid #00b050',
        height: 0,
        width: 0,
        borderRadius: '4px',
      }}
    />
  </div>
);
